/**
 * Ephemeral armed-target state for measuring directly into the active sketch.
 *
 * The durable MeasurementSession remains useful as an audit/review record, but
 * it is not the interaction contract for in-sketch Field Capture. A transport
 * reading may reach the model only through this state, after the designer has
 * selected one visible measurement target.
 */

const TARGET_PROPERTIES = Object.freeze({
  wall_length: new Set(['lengthUnits']),
  space_field: new Set(['ceilingHeightUnits', 'doorHeightUnits']),
  wall_field: new Set(['wallThicknessUnits', 'partitionDepthUnits', 'partitionThicknessUnits', 'doorWidthUnits']),
  wall_height_field: new Set(['startHeightUnits', 'endHeightUnits', 'stackTargetUnits']),
  wall_height_draft: new Set(['breakAtUnits', 'breakHeightUnits']),
  ceiling_opening_draft: new Set([
    // KILLED BY tests/measurement/room-sketch-bluetooth-targets.test.js — the
    // creation-form pin exercises every property while the controls stay wired.
    'xUnits', 'yUnits', 'widthUnits', 'lengthUnits', 'heightUnits',
    'fromFloorUnits', 'ladderFloorOffsetUnits',
  ]),
  design_space_field: new Set(['spaceHeightUnits']),
  section_field: new Set(['pairWidthUnits']),
  storage_need_field: new Set(['existingUnits', 'neededUnits']),
  footwear_field: new Set(['widthUnits', 'lengthUnits']),
  opening_field: new Set([
    'referenceOffset', 'offsetFromFloor', 'widthUnits', 'heightUnits',
    'depthUnits', 'faceOffsetUnits', 'clearOpeningWidthUnits', 'clearOpeningHeightUnits', 'swingProjectionUnits', 'bifoldStackProjectionUnits',
    'bypassClearLeftUnits', 'bypassClearRightUnits',
  ]),
  feature_field: new Set([
    'referenceOffset', 'offsetFromFloor', 'widthUnits', 'heightUnits',
    'depthUnits', 'faceOffsetUnits',
  ]),
  ceiling_opening_field: new Set([
    // KILLED BY tests/measurement/room-sketch-bluetooth-targets.test.js — the
    // saved-card pin exercises every decorated property, including both fans.
    'xUnits', 'yUnits', 'widthUnits', 'lengthUnits', 'heightUnits', 'ladderFloorOffsetUnits', 'ladderProjectionUnits',
    'diameterUnits', 'fromFloorUnits',
  ]),
  // A row of the Engineering remeasure form (owner, 2026-08-28): the entity
  // is the remeasure MEASUREMENT KEY (e.g. "wall:w1:length"), and the single
  // property is the row's Actual value — readings land in the remeasure
  // session through setRemeasureValue, never in the live sketch.
  remeasure_field: new Set(['value']),
});

/**
 * Validates a visible destination before either decoration or arming. Keeping
 * this public lets the UI refuse a dead affordance before it looks actionable.
 */
export function validateFieldCaptureTarget(target) {
  const properties = TARGET_PROPERTIES[target?.kind];
  if (!properties || !target.entityId || !properties.has(target.property) || !String(target.label ?? '').trim()) {
    throw new Error('Choose a valid visible measurement target to arm.');
  }
  return {
    kind: target.kind,
    entityId: target.entityId,
    property: target.property,
    label: String(target.label).trim(),
  };
}

/**
 * Creates inactive app-level Field Capture state.
 *
 * The candidate list PERSISTS (serialize/restore below) — a ruling reversal,
 * 2026-08-29: this header used to say "deliberately not persisted", and the
 * cost of that was the owner's exact nightmare — three readings taken in a
 * client's closet existing nowhere but a killable tab. The armed target and
 * active flag stay transient: restore always comes back disarmed, so the
 * standing safety invariant (a transport event has no destination unless the
 * designer just chose one) survives persistence.
 */
export function createFieldCaptureMode() {
  return {
    active: false,
    armedTarget: null,
    lastReading: null,
    readings: [],
    selectedReadingIds: {},
    confirmedTargetReadingIds: {},
    correctionTargetKey: null,
  };
}

/** A JSON-safe copy of the whole capture state for durable storage. */
export function serializeFieldCaptureMode(mode) {
  return JSON.parse(JSON.stringify(mode ?? createFieldCaptureMode()));
}

/**
 * Rebuilds capture state from a stored payload — validated whole, DISARMED.
 * Any malformed part returns null (never a partial restore): a payload this
 * function accepts is one every other function in this module can trust, so
 * the validation here is the same validateFieldCaptureTarget the live path uses.
 */
export function restoreFieldCaptureMode(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const readings = payload.readings ?? [];
  if (!Array.isArray(readings)) return null;
  try {
    const restored = {
      ...createFieldCaptureMode(),
      readings: readings.map((reading) => {
        if (!reading?.id || !Number.isFinite(reading.valueUnits) || reading.valueUnits <= 0) {
          throw new Error('invalid reading');
        }
        return {
          id: String(reading.id),
          target: validateFieldCaptureTarget(reading.target),
          valueUnits: reading.valueUnits,
          source: String(reading.source ?? 'manual'),
          capturedAt: String(reading.capturedAt ?? ''),
        };
      }),
      selectedReadingIds: { ...(payload.selectedReadingIds ?? {}) },
      confirmedTargetReadingIds: { ...(payload.confirmedTargetReadingIds ?? {}) },
      lastReading: payload.lastReading ?? null,
    };
    return restored;
  } catch {
    return null;
  }
}

/** Enters Field Capture without choosing a destination on the designer's behalf. */
export function activateFieldCapture(mode = createFieldCaptureMode()) {
  return { ...mode, active: true, armedTarget: null, correctionTargetKey: null };
}

/** Leaves Field Capture and guarantees that a later transport event has no destination. */
export function deactivateFieldCapture(mode) {
  return { ...mode, active: false, armedTarget: null, correctionTargetKey: null };
}

/** Arms exactly one explicitly named sketch/input target, replacing any prior target. */
export function armFieldCaptureTarget(mode, target) {
  if (!mode?.active) throw new Error('Enter Field Capture before arming a target.');
  const armedTarget = validateFieldCaptureTarget(target);
  const sameTarget = mode.armedTarget && targetKey(mode.armedTarget) === targetKey(armedTarget);
  return {
    ...mode,
    armedTarget,
    correctionTargetKey: sameTarget ? mode.correctionTargetKey ?? null : null,
  };
}

/** Disarms the current target while leaving Field Capture active. */
export function disarmFieldCaptureTarget(mode) {
  return { ...mode, armedTarget: null, correctionTargetKey: null };
}

function targetKey(target) {
  const valid = validateFieldCaptureTarget(target);
  return `${valid.kind}:${valid.entityId}:${valid.property}`;
}

function readingId(mode, makeId) {
  if (typeof makeId === 'function') return String(makeId());
  return `capture-${Date.now()}-${(mode.readings?.length ?? 0) + 1}`;
}

/**
 * Keeps a reading as an explicitly reviewable candidate for the armed field.
 * The newest reading becomes selected, but no sketch value changes until the
 * designer applies that selection.
 */
export function captureFieldReading(mode, valueUnits, {
  source = 'manual', makeId, nowIso = new Date().toISOString(),
} = {}) {
  if (!mode?.active) throw new Error('Enter Field Capture before receiving a reading.');
  if (!mode.armedTarget) throw new Error('Select and arm a visible measurement target before receiving a reading.');
  if (!Number.isFinite(valueUnits) || valueUnits <= 0) throw new Error('Measurement value must be a positive number.');
  const target = validateFieldCaptureTarget(mode.armedTarget);
  const key = targetKey(target);
  if (mode.confirmedTargetReadingIds?.[key] && mode.correctionTargetKey !== key) {
    throw new Error('This target already has a confirmed measurement. Choose Correct before replacing it.');
  }
  const reading = {
    id: readingId(mode, makeId),
    target: { ...target },
    valueUnits,
    source,
    capturedAt: nowIso,
  };
  return {
    ...mode,
    readings: [...(mode.readings ?? []), reading],
    selectedReadingIds: { ...(mode.selectedReadingIds ?? {}), [key]: reading.id },
  };
}

/** Candidates for the current target, always presented low-to-high. */
export function readingsForArmedTarget(mode) {
  if (!mode?.armedTarget) return [];
  const key = targetKey(mode.armedTarget);
  return (mode.readings ?? [])
    .filter((reading) => targetKey(reading.target) === key)
    .sort((a, b) => a.valueUnits - b.valueUnits
      || String(a.capturedAt).localeCompare(String(b.capturedAt))
      || String(a.id).localeCompare(String(b.id)));
}

/** The candidate currently chosen for the armed target, if any. */
export function selectedReadingForArmedTarget(mode) {
  if (!mode?.armedTarget) return null;
  const selectedId = mode.selectedReadingIds?.[targetKey(mode.armedTarget)];
  return readingsForArmedTarget(mode).find((candidate) => candidate.id === selectedId) ?? null;
}

/** Selects one candidate belonging to the currently armed target. */
export function selectFieldCaptureReading(mode, id) {
  const reading = readingsForArmedTarget(mode).find((candidate) => candidate.id === id);
  if (!reading) throw new Error('Choose a reading for the armed target.');
  return {
    ...mode,
    selectedReadingIds: { ...(mode.selectedReadingIds ?? {}), [targetKey(mode.armedTarget)]: id },
  };
}

/** Deletes one obvious bad reading without affecting candidates for other targets. */
export function deleteFieldCaptureReading(mode, id) {
  const current = readingsForArmedTarget(mode);
  if (!current.some((reading) => reading.id === id)) throw new Error('Choose a reading for the armed target.');
  const readings = (mode.readings ?? []).filter((reading) => reading.id !== id);
  const key = targetKey(mode.armedTarget);
  const selectedReadingIds = { ...(mode.selectedReadingIds ?? {}) };
  if (selectedReadingIds[key] === id) {
    const remaining = current.filter((reading) => reading.id !== id);
    if (remaining.length) selectedReadingIds[key] = remaining[0].id;
    else delete selectedReadingIds[key];
  }
  return { ...mode, readings, selectedReadingIds };
}

/** Applies the selected candidate through the target's canonical model setter. */
export function applySelectedFieldCaptureReading(mode, { applyTarget } = {}) {
  if (!mode?.armedTarget) throw new Error('Select and arm a visible measurement target before applying a reading.');
  const reading = selectedReadingForArmedTarget(mode);
  if (!reading) throw new Error('Choose a captured reading before applying it.');
  const next = applyFieldCaptureReading(mode, reading.valueUnits, { source: reading.source, applyTarget });
  const key = targetKey(reading.target);
  return {
    ...next,
    lastReading: { ...next.lastReading, candidateId: reading.id },
    confirmedTargetReadingIds: { ...(next.confirmedTargetReadingIds ?? {}), [key]: reading.id },
    correctionTargetKey: null,
  };
}

/** Whether the armed destination already has an applied/confirmed reading. */
export function isArmedFieldCaptureTargetConfirmed(mode) {
  if (!mode?.armedTarget) return false;
  return Boolean(mode.confirmedTargetReadingIds?.[targetKey(mode.armedTarget)]);
}

/** A confirmed destination accepts no transport input until correction is explicit. */
export function isArmedFieldCaptureTargetLocked(mode) {
  if (!isArmedFieldCaptureTargetConfirmed(mode)) return false;
  const key = targetKey(mode.armedTarget);
  return mode.correctionTargetKey !== key;
}

/** Unlocks only the currently armed confirmed target for an intentional replacement. */
export function beginArmedFieldCaptureCorrection(mode) {
  if (!mode?.active || !mode.armedTarget) throw new Error('Select a confirmed measurement target before correcting it.');
  const key = targetKey(mode.armedTarget);
  if (!mode.confirmedTargetReadingIds?.[key]) throw new Error('Apply a measurement before using Correct.');
  return { ...mode, correctionTargetKey: key };
}

/**
 * Routes a positive reading to the one armed target. `applyTarget` owns the
 * canonical model mutation; returning false rejects the reading. The returned
 * state records what visibly changed but keeps the target armed for an explicit
 * retake of the same field.
 */
export function applyFieldCaptureReading(mode, valueUnits, { source = 'manual', applyTarget } = {}) {
  if (!mode?.active) throw new Error('Enter Field Capture before receiving a reading.');
  if (!mode.armedTarget) throw new Error('Select and arm a visible measurement target before receiving a reading.');
  if (!Number.isFinite(valueUnits) || valueUnits <= 0) throw new Error('Measurement value must be a positive number.');
  if (typeof applyTarget !== 'function') throw new Error('A canonical sketch measurement writer is required.');
  const target = validateFieldCaptureTarget(mode.armedTarget);
  if (applyTarget(target, valueUnits) === false) throw new Error('The armed measurement target rejected this reading.');
  return {
    ...mode,
    lastReading: { target: { ...target }, valueUnits, source, capturedAt: new Date().toISOString() },
  };
}
